import hljs from 'highlight.js';
import React, { memo, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Tooltip } from '@/components/Tooltip';
import { Message } from '@/types';
import { copyToClipboard } from '@/utils/copyToClipboard.util';

import ChatErrorNotification from './ChatErrorNotification';
import ChatResultsTable from './ChatResultsTable';
import ChatToolStatus from './ChatToolStatus';

interface ChatMessageProps {
  message: Message;
  pairedResponse?: Message | null;
}

// Detect if content is JSON with array data or error data
const getJsonData = (content: string) => {
  try {
    if (content.trim().startsWith('{') || content.trim().startsWith('[{')) {
      const jsonData = JSON.parse(content);

      // Handle direct arrays (like job arrays with capabilities)
      if (Array.isArray(jsonData) && jsonData.length > 0) {
        const firstItem = jsonData[0];
        // Check if it looks like a job or capability-related object
        if (
          firstItem &&
          typeof firstItem === 'object' &&
          (firstItem.capabilities ||
            firstItem.source ||
            firstItem.status?.includes('JQ') ||
            firstItem.key?.includes('#job#'))
        ) {
          return { type: 'table', data: { jobs: jsonData } };
        }
        // Check if it's another type of entity array
        if (firstItem && typeof firstItem === 'object') {
          return { type: 'table', data: { data: jsonData } };
        }
      }

      // Check for error responses
      if (jsonData.error && jsonData.tool) {
        return { type: 'error', data: jsonData };
      }

      // Filter out meaningless count-only responses
      if (
        typeof jsonData.count === 'number' &&
        Object.keys(jsonData).length === 1
      ) {
        // Skip rendering empty count responses like {"count": 0}
        return null;
      }

      // Check for tool status responses (count + error fields)
      if (typeof jsonData.count === 'number' && 'error' in jsonData) {
        return { type: 'tool-status', data: jsonData };
      }

      // Check for array data (tables) - only show tables for clean, obvious arrays
      const topLevelArrays = Object.entries(jsonData).filter(
        ([_key, value]) => Array.isArray(value) && value.length > 0
      );

      // Show table if we have a clear top-level array with common entity names, otherwise show json
      const tableWorthy = topLevelArrays.some(([key]) =>
        [
          'assets',
          'risks',
          'vulnerabilities',
          'technologies',
          'attributes',
          'capabilities',
          'repositories',
          'webpages',
          'jobs',
          'available_types',
        ].includes(key)
      );

      if (tableWorthy) {
        return { type: 'table', data: jsonData };
      }

      // For complex JSON that's not table-worthy, show as formatted JSON
      return { type: 'json-only', data: jsonData };
    }
  } catch {
    // Not valid JSON
  }
  return null;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  pairedResponse,
}) => {
  const isUser = message.role === 'user';
  const isToolCall = message.role === 'tool call';
  const isToolResponse = message.role === 'tool response';
  const [isToolExpanded, setIsToolExpanded] = useState(false);

  const jsonData = useMemo(() => {
    if (isUser || isToolCall || isToolResponse) return null;
    return getJsonData(message.content);
  }, [message.content, isUser, isToolCall, isToolResponse]);

  // Hide the generic "I need to use a tool" message ONLY if it's not a tool call with pairedResponse
  // (if it has pairedResponse, we want to show it as a tool pair)
  const isGenericToolMessage =
    !isUser &&
    !isToolCall &&
    message.content === 'I need to use a tool to answer your question.';
  const isTemporary = isGenericToolMessage;

  // Don't render temporary/status messages - just show loading indicator instead
  if (isTemporary) {
    return null;
  }

  // Filter out meaningless count-only JSON responses
  if (!isUser && !jsonData && message.content) {
    try {
      const parsed = JSON.parse(message.content.trim());
      if (
        typeof parsed.count === 'number' &&
        Object.keys(parsed).length === 1
      ) {
        return null; // Don't render empty count responses
      }
    } catch {
      // Not JSON or parsing failed, continue normally
    }
  }

  // Show tool call/response pair horizontally (pairedResponse is passed from parent for tool calls)
  if (isToolCall && pairedResponse !== undefined) {
    // Parse tool call data
    const getToolData = (msg: Message) => {
      const toolContent = (msg as any).toolUseContent;
      if (toolContent) {
        try {
          return JSON.parse(toolContent);
        } catch {
          // Fall through
        }
      }

      try {
        return JSON.parse(msg.content);
      } catch {
        return null;
      }
    };

    const toolData = getToolData(message);
    const toolName =
      toolData?.Name || toolData?.tool || toolData?.name || 'tool';

    // Helper to format a single filter for display
    const formatFilter = (f: any): string => {
      // Handle OR/AND operators with nested filters
      if (
        (f.operator === 'OR' || f.operator === 'AND') &&
        Array.isArray(f.value)
      ) {
        const nestedFilters = f.value
          .map((nested: any) => formatFilter(nested))
          .join(`, `);
        return `${f.operator} [${nestedFilters}]`;
      }

      // Handle regular filter with field/operator/value
      const val = f.value;
      let formattedValue: string;
      if (Array.isArray(val)) {
        formattedValue = `[${val.join(', ')}]`;
      } else if (val && typeof val === 'object') {
        formattedValue = JSON.stringify(val);
      } else {
        formattedValue = String(val);
      }

      return `${f.field} ${f.operator} ${formattedValue}`;
    };

    // Create display label for tool call - use actual API data
    const getToolLabel = () => {
      if (!toolData) return 'tool call';

      const input = toolData.Input || toolData.input || toolData.params;

      // For query tool, describe the actual query being performed
      if (toolName === 'query' && input?.node) {
        const labels = input.node.labels || [];
        const filters = input.node.filters || [];
        const relationships = input.node.relationships || [];
        const orderBy = input.orderBy;
        const limit = input.limit;

        let description = labels.length > 0 ? labels.join(', ') : 'entities';

        // Add filter information if present
        if (filters.length > 0) {
          const filterDesc = filters
            .map((f: any) => formatFilter(f))
            .join(', ');
          description += ` where ${filterDesc}`;
        }

        // Add relationship information if present
        if (relationships.length > 0) {
          const relDesc = relationships
            .map((r: any) => {
              const targetLabels = r.target?.labels?.join(', ') || 'related';
              const targetFilters = r.target?.filters || [];
              if (targetFilters.length > 0) {
                const filterStr = targetFilters
                  .map((f: any) => formatFilter(f))
                  .join(', ');
                return `with ${targetLabels} (${filterStr})`;
              }
              return `with ${targetLabels}`;
            })
            .join(', ');
          description += ` ${relDesc}`;
        }

        // Add ordering if present
        if (orderBy) {
          description += ` ordered by ${orderBy}`;
        }

        // Add limit if present
        if (limit) {
          description += ` (limit ${limit})`;
        }

        return description;
      }

      // For schema tool
      if (toolName === 'schema' && input?.entity_types) {
        return `schema(${input.entity_types.join(', ')})`;
      }

      // For other tools, just use the tool name
      return toolName;
    };

    // Parse response data
    const responseData = pairedResponse
      ? (() => {
          try {
            return JSON.parse(pairedResponse.content);
          } catch {
            return null;
          }
        })()
      : null;

    const hasError = responseData && responseData.error;

    return (
      <div
        className="border-chariot-stroke/30 my-1 ml-4 border-l-2 pl-3"
        data-message-id={message.id || message.key}
      >
        <button
          onClick={() => setIsToolExpanded(!isToolExpanded)}
          className="flex cursor-pointer items-center gap-3 text-left text-xs transition-opacity hover:opacity-80"
        >
          {/* Request */}
          <span className="text-chariot-text-tertiary flex-shrink-0">→</span>
          <span className="text-chariot-text-secondary decoration-chariot-text-quaternary/40 underline decoration-dotted underline-offset-2">
            {getToolLabel()}
          </span>

          {/* Response */}
          {pairedResponse && (
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {hasError ? (
                <>
                  <span className="text-red-400">✗</span>
                  <span className="text-red-400/80 underline decoration-red-400/40 decoration-dotted underline-offset-2">
                    error
                  </span>
                </>
              ) : (
                <>
                  <span className="text-green-400/70">✓</span>
                  <span className="text-chariot-text-quaternary/70 decoration-chariot-text-quaternary/40 underline decoration-dotted underline-offset-2">
                    {responseData?.count !== undefined
                      ? `${responseData.count} results`
                      : 'done'}
                  </span>
                </>
              )}
            </div>
          )}

          {!pairedResponse && (
            <span className="text-chariot-text-quaternary/50 flex-shrink-0 text-[10px]">
              waiting...
            </span>
          )}
        </button>

        {pairedResponse && responseData && (
          <div
            className={`mt-2 overflow-hidden transition-[max-height] duration-200 ${
              isToolExpanded ? 'max-h-[800px]' : 'max-h-0'
            }`}
          >
            {/* If error, show error message */}
            {hasError ? (
              <div className="border-chariot-stroke rounded border border-l-2 border-l-red-500/50 p-3">
                <div className="mb-1 text-xs font-medium text-red-400">
                  Error
                </div>
                <div className="text-chariot-text-tertiary text-xs break-words whitespace-pre-wrap">
                  {responseData.error}
                </div>
              </div>
            ) : null}
            {/* If response has data arrays, show as table */}
            {(() => {
              const arrays = Object.entries(responseData).filter(
                ([_k, v]) => Array.isArray(v) && v.length > 0
              );
              if (arrays.length > 0) {
                const [key, data] = arrays[0];
                return <ChatResultsTable jsonData={{ [key]: data }} />;
              }
              // Otherwise show as JSON with syntax highlighting (if not error)
              if (!hasError) {
                const jsonString = JSON.stringify(responseData, null, 2);
                const highlighted = hljs.highlight(jsonString, {
                  language: 'json',
                  ignoreIllegals: true,
                }).value;

                return (
                  <div className="border-chariot-stroke max-h-60 overflow-y-auto rounded border p-3">
                    <pre className="font-mono text-xs break-words whitespace-pre-wrap">
                      <code
                        className="hljs language-json !bg-transparent"
                        dangerouslySetInnerHTML={{ __html: highlighted }}
                      />
                    </pre>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    );
  }

  // Don't render orphaned tool responses
  if (isToolResponse) {
    return null;
  }

  return (
    <div className="mb-2">
      {isUser ? (
        <div className="bg-chariot-stroke rounded-lg px-4 py-3">
          <div className="text-chariot-text-primary text-[15px] leading-[1.6] font-medium break-words whitespace-pre-wrap">
            {message.content.replace(/\n\n+/g, '\n')}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div
            className={`bg-chariot-brand-primary/10 -ml-1.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full ${
              message.content.trim().match(/^#{1,6}\s/) ? 'mt-[1.4rem]' : 'mt-1'
            }`}
          >
            <div className="bg-chariot-brand-primary h-2 w-2 rounded-full"></div>
          </div>
          <div className="text-chariot-text-primary min-w-0 flex-1 text-[15px] leading-[1.6]">
            {jsonData ? (
              <div>
                {/* Handle different JSON response types */}
                {jsonData.type === 'error' ? (
                  <ChatErrorNotification error={jsonData.data} />
                ) : jsonData.type === 'tool-status' ? (
                  <ChatToolStatus data={jsonData.data} />
                ) : jsonData.type === 'json-only' ? (
                  <div className="relative">
                    <div className="absolute top-2 right-2 z-10">
                      <CopyToClipboard
                        textToCopy={JSON.stringify(jsonData.data, null, 2)}
                      />
                    </div>
                    <pre className="border-chariot-stroke overflow-x-auto rounded-lg border p-3 pr-12 text-xs">
                      {JSON.stringify(jsonData.data, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <ChatResultsTable jsonData={jsonData.data} />
                )}
              </div>
            ) : (
              <div className="group/message">
                <MarkdownPreview
                  source={message.content}
                  className="prose prose-sm [&_h1]:text-chariot-text-primary [&_h2]:text-chariot-text-primary [&_h3]:text-chariot-text-primary [&_p]:text-chariot-text-secondary [&_li]:text-chariot-text-secondary [&_code:not(.hljs)]:bg-chariot-stroke [&_hr]:border-chariot-stroke [&_strong]:text-chariot-text-primary [&_em]:text-chariot-text-primary max-w-none [&_code:not(.hljs)]:rounded [&_code:not(.hljs)]:px-1.5 [&_code:not(.hljs)]:py-0.5 [&_code:not(.hljs)]:font-mono [&_code:not(.hljs)]:text-sm [&_em]:italic [&_h1]:mt-6 [&_h1]:mb-4 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mt-5 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_hr]:my-4 [&_hr]:border-t [&_li]:leading-relaxed [&_ol]:mb-4 [&_ol]:ml-4 [&_ol]:space-y-2 [&_p]:mb-3 [&_p]:leading-relaxed [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_strong]:font-semibold [&_ul]:mb-4 [&_ul]:ml-4 [&_ul]:space-y-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                />
                <Tooltip title="Copy to clipboard">
                  <button
                    onClick={() => {
                      copyToClipboard(message.content);
                      toast.success('Copied to clipboard', { duration: 1500 });
                    }}
                    className="text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-stroke/30 -mt-2 mb-4 -ml-2 flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ChatMessage);
